import { useNavigate } from 'react-router-dom';
import { ChangeEvent, useState, useEffect } from 'react';
import useLoginContext from './useLoginContext';
import {
  createUser,
  loginUser,
  guestLogin,
  completeSignup,
  refreshUserData,
} from '../services/userService';
import { SafeDatabaseUser } from '../types/types';
import { ExtendedUserCredentials } from '../types/extendedTypes';
import { handleProviderAuth, AuthProviderType } from '../utils/authProviders';

/**
 * Custom hook to manage authentication logic, including handling input changes,
 * form submission, password visibility toggling, and error validation for both
 * login and signup processes.
 *
 * @param authType - Specifies the authentication type ('login' or 'signup').
 * @param returnUrl - Optional URL to redirect to after successful authentication
 * @returns {Object} An object containing:
 *   - username: The current value of the username input.
 *   - password: The current value of the password input.
 *   - email: The current value of the email input (for signup).
 *   - passwordConfirmation: The current value of the password confirmation input (for signup).
 *   - showPassword: Boolean indicating whether the password is visible.
 *   - err: The current error message, if any.
 *   - setErr: Function to set the error message.
 *   - handleInputChange: Function to handle changes in input fields.
 *   - handleSubmit: Function to handle form submission.
 *   - togglePasswordVisibility: Function to toggle password visibility.
 *   - requires2FA: Boolean indicating if 2FA verification is required.
 *   - pending2FAUsername: Username pending 2FA verification.
 *   - setPending2FAUsername: Function to set the pending 2FA username.
 *   - setRequires2FA: Function to set the 2FA requirement status.
 *   - completeLoginWith2FA: Function to complete login after 2FA verification.
 *   - isLoading: Boolean indicating if the login/signup process is in progress.
 *   - isGuestLoading: Boolean indicating if the guest login process is in progress.
 *   - signupToken: Token for completing signup after 2FA verification.
 *   - completeSignupWith2FA: Function to complete signup after 2FA verification.
 *   - handleProviderSignIn: Function to handle third-party authentication (Google, GitHub, LinkedIn)
 *   - getPasswordStrength: Function to get the strength of a password
 *   - getPasswordStrengthMessage: Function to get a user-friendly message about how to improve password strength
 *   - handleGuest: Function to handle guest login
 */
const useAuth = (authType: 'login' | 'signup', returnUrl: string = '/home') => {
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [passwordConfirmation, setPasswordConfirmation] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState<string>('');
  const [requires2FA, setRequires2FA] = useState<boolean>(false);
  const [pending2FAUsername, setPending2FAUsername] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isGuestLoading, setIsGuestLoading] = useState<boolean>(false);
  const [signupToken, setSignupToken] = useState<string>('');
  const { user, setUser } = useLoginContext();
  const navigate = useNavigate();

  // If user is already logged in, redirect to home page
  useEffect(() => {
    if (user) {
      navigate('/home');
    }
  }, [user, navigate]);

  /**
   * Toggles the visibility of the password input field.
   */
  const togglePasswordVisibility = () => {
    setShowPassword(prevState => !prevState);
  };

  /**
   * Handles changes in input fields and updates the corresponding state.
   *
   * @param e - The input change event.
   * @param field - The field being updated ('username', 'password', 'email', or 'confirmPassword').
   */
  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement>,
    field: 'username' | 'password' | 'confirmPassword' | 'email',
  ) => {
    const fieldText = e.target.value.trim();

    if (field === 'username') {
      setUsername(fieldText);
    } else if (field === 'password') {
      setPassword(fieldText);
    } else if (field === 'confirmPassword') {
      setPasswordConfirmation(fieldText);
    } else if (field === 'email') {
      setEmail(fieldText);
    }
  };

  /**
   * get the strength of the password
   * @param password - The password to check.
   * @returns {string} The strength of the password ('Weak', 'Medium', or 'Strong').
   */
  const getPasswordStrength = (inputPassword: string): string => {
    if (!inputPassword || inputPassword.length < 6) return 'Weak';

    // Strong: Minimum 8 characters, at least 1 uppercase letter, 1 number, and 1 special character
    if (/^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(inputPassword))
      return 'Strong';

    // Medium: Minimum 6 characters, at least 1 uppercase letter and 1 number
    if (/^(?=.*[A-Z])(?=.*\d)[A-Za-z\d@$!%*?&]{6,}$/.test(inputPassword)) return 'Medium';

    // If it doesn't meet any of the above, default to "Weak"
    return 'Weak';
  };

  /**
   * Gets a user-friendly message about how to improve password strength
   * @param password - The password to check
   * @returns {string} A message about how to improve password strength
   */
  const getPasswordStrengthMessage = (inputPassword: string): string => {
    if (!inputPassword || inputPassword.length < 6) {
      return 'Password should be at least 6 characters';
    }

    const hasUpperCase = /[A-Z]/.test(inputPassword);
    const hasNumber = /\d/.test(inputPassword);
    const hasSpecial = /[@$!%*?&]/.test(inputPassword);
    const isLongEnough = inputPassword.length >= 8;

    if (!isLongEnough) {
      return 'Password should be at least 8 characters for better security';
    }

    if (!hasUpperCase) {
      return 'Add an uppercase letter to strengthen your password';
    }

    if (!hasNumber) {
      return 'Add a number to strengthen your password';
    }

    if (!hasSpecial) {
      return 'Add a special character (@$!%*?&) to strengthen your password';
    }

    return '';
  };

  /**
   * Validates the input fields for the form.
   * Ensures required fields are filled and passwords match (for signup).
   *
   * @returns {boolean} True if inputs are valid, false otherwise.
   */
  const validateInputs = (): boolean => {
    if (username === '' || password === '') {
      setErr('Please enter a username and password');
      return false;
    }

    if (authType === 'signup') {
      if (password !== passwordConfirmation) {
        setErr('Passwords do not match');
        return false;
      }

      if (email === '') {
        setErr('Please enter an email address');
        return false;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setErr('Please enter a valid email address');
        return false;
      }

      const passwordStrength = getPasswordStrength(password);
      if (passwordStrength !== 'Strong') {
        setErr(
          'Password must contain a minimum 8 characters, at least 1 uppercase letter, 1 number, and 1 special character',
        );
        return false;
      }
    }

    return true;
  };

  /**
   * Completes the login process after 2FA verification.
   *
   * @param userData - The user data returned after 2FA verification.
   */
  const completeLoginWith2FA = (userData: SafeDatabaseUser) => {
    setUser(userData);
    navigate(returnUrl);
  };

  /**
   * Completes the signup process after 2FA verification
   *
   * @param code - The verification code
   */
  const completeSignupWith2FA = async (code: string): Promise<SafeDatabaseUser | undefined> => {
    if (!code || code.length !== 6) {
      setErr('Please enter a valid 6-digit verification code');
      return undefined;
    }

    try {
      setIsLoading(true);
      const userData = await completeSignup(pending2FAUsername, code, signupToken);
      return userData;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to verify code';
      setErr(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handles the submission of the form.
   * Validates input, performs login/signup, and navigates to the home page on success.
   *
   * @param event - The form submission event.
   * @param providerAuth - Optional provider authentication data
   */
  const handleSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
    providerAuth?: {
      username: string;
      password: string;
      email?: string;
      isProviderAuth: boolean;
      provider?: string;
    },
  ) => {
    event.preventDefault();

    // If using Provider Auth, skip validation and use provided credentials
    if (providerAuth) {
      try {
        setIsLoading(true);
        let userData;

        if (authType === 'signup') {
          userData = await createUser({
            username: providerAuth.username,
            password: providerAuth.password,
            email: providerAuth.email || `${providerAuth.username}@gmail.com`,
          } as ExtendedUserCredentials);

          // For signup, check if 2FA verification is required
          if (userData && userData.requiresTwoFactor) {
            setRequires2FA(true);
            setPending2FAUsername(providerAuth.username);
            // Store the signup token for completing the process
            if (userData.signupToken) {
              setSignupToken(userData.signupToken);
            }
            setIsLoading(false);
            return;
          }
        } else if (authType === 'login') {
          userData = await loginUser({
            username: providerAuth.username,
            password: providerAuth.password,
          });
        }

        if (userData) {
          // Check if 2FA verification is required
          if (userData.requiresTwoFactor) {
            setRequires2FA(true);
            setPending2FAUsername(providerAuth.username);
            setIsLoading(false);
            return;
          }

          // Refresh user data to ensure we have the latest isAdmin status
          if (authType === 'login') {
            try {
              const refreshedUserData = await refreshUserData(userData.username);
              setUser(refreshedUserData);
            } catch (refreshError) {
              // If refresh fails, still use the original userData
              setUser(userData);
            }
          } else {
            setUser(userData);
          }

          navigate(returnUrl);
        }
      } catch (error) {
        setErr((error as Error).message);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Regular form validation for non-Google auth
    if (!validateInputs()) {
      return;
    }

    try {
      setIsLoading(true);
      let userData;

      if (authType === 'signup') {
        userData = await createUser({
          username,
          password,
          email,
        } as ExtendedUserCredentials);

        // For signup with email, we need to prompt for 2FA verification
        if (userData && userData.requiresTwoFactor) {
          setRequires2FA(true);
          setPending2FAUsername(username);
          // Store the signup token for completing the process
          if (userData.signupToken) {
            setSignupToken(userData.signupToken);
          }
          return;
        }
      } else if (authType === 'login') {
        userData = await loginUser({ username, password });

        // Check if 2FA verification is required
        if (userData.requiresTwoFactor) {
          setRequires2FA(true);
          setPending2FAUsername(username);
          return;
        }
      } else {
        throw new Error('Invalid auth type');
      }

      if (userData) {
        // Refresh user data to ensure we have the latest isAdmin status
        try {
          const refreshedUserData = await refreshUserData(userData.username);
          setUser(refreshedUserData);
        } catch (refreshError) {
          // If refresh fails, still use the original userData
          setUser(userData);
        }
        navigate(returnUrl);
      }
    } catch (error) {
      setErr((error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handles authentication with a third-party provider like Google, GitHub, or LinkedIn
   * @param provider The authentication provider to use
   */
  const handleProviderSignIn = async (provider: AuthProviderType) => {
    try {
      setIsLoading(true);

      const authData = await handleProviderAuth(provider);

      // Create synthetic event to use with existing handleSubmit logic
      const syntheticEvent = {
        preventDefault: () => {},
      } as React.FormEvent<HTMLFormElement>;

      // Call handleSubmit with the provider credentials
      await handleSubmit(syntheticEvent, {
        username: authData.username,
        password: authData.password,
        email: authData.email,
        isProviderAuth: true,
        provider: authData.provider,
      });
    } catch (error) {
      setErr(`${provider} sign-in error: ${(error as Error).message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGuest = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    try {
      setIsGuestLoading(true);

      // Check if we already have a guest user in localStorage
      const cachedGuestUser = localStorage.getItem('guestUser');

      if (cachedGuestUser) {
        try {
          // Parse and use the cached guest user data
          const guestUserData = JSON.parse(cachedGuestUser);

          // Check if the guest user has an expiration and if it's not expired
          // Default expiration time: 24 hours (for future implementation)
          const now = new Date().getTime();
          const cachedTime = guestUserData.cachedAt || 0;
          const expirationTime = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

          if (now - cachedTime < expirationTime) {
            // Use the cached guest user if not expired
            setUser(guestUserData.user);
            navigate(returnUrl);
            return;
          }

          // If expired, remove from localStorage and continue to create a new guest user
          localStorage.removeItem('guestUser');
        } catch (error) {
          // If there's an error parsing the cached user, remove it
          localStorage.removeItem('guestUser');
        }
      }

      // Create a new guest user
      const userData = await guestLogin();

      if (userData) {
        // Cache the guest user data with timestamp
        localStorage.setItem(
          'guestUser',
          JSON.stringify({
            user: userData,
            cachedAt: new Date().getTime(),
          }),
        );

        setUser(userData);
        navigate(returnUrl);
      }
    } catch (error) {
      setErr((error as Error).message);
    } finally {
      setIsGuestLoading(false);
    }
  };

  return {
    username,
    password,
    email,
    passwordConfirmation,
    showPassword,
    err,
    setErr,
    handleInputChange,
    handleSubmit,
    togglePasswordVisibility,
    requires2FA,
    pending2FAUsername,
    setPending2FAUsername,
    setRequires2FA,
    completeLoginWith2FA,
    isLoading,
    isGuestLoading,
    signupToken,
    completeSignupWith2FA,
    getPasswordStrength,
    getPasswordStrengthMessage,
    handleGuest,
    handleProviderSignIn,
  };
};

export default useAuth;
